import React from "react";
import { KeyTerm, NeurotransmitterCard, BrainRegionCard, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const psychologyUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Biological Bases of Behavior",
  examWeight: "15–25%",
  focus: "How physiological systems (nervous/endocrine) relate to behavior and mental processes. The focus is on function rather than just anatomy.",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Interaction of Heredity and Environment",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Nature vs. Nurture</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Psychology examines the interplay between our genetics and our lived experiences.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <KeyTerm 
                term="Heredity (Nature)" 
                definition="Genetic transfer of characteristics from parents to offspring. It predisposes us to certain physical, behavioral, and mental traits (e.g., temperament, susceptibility to diseases)."
              />
              <KeyTerm 
                term="Environment (Nurture)" 
                definition="Every non-genetic influence, from prenatal nutrition to the people and things around us (e.g., culture, education, peer influence)."
              />
            </div>
            <KeyTerm 
              term="Interaction" 
              definition="Genes are not destiny; they are self-regulating. Environments trigger gene activity (epigenetics)."
            />
            <ImportantNote>
              <strong>Example:</strong> A child may have a genetic predisposition for high intelligence (nature), but without access to education and nutrition (nurture), that potential may not be reached.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Evolutionary Psychology</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This perspective uses Charles Darwin's principle of <strong>Natural Selection</strong> to explain behavior.
            </p>
            <KeyTerm 
              term="Core Concept" 
              definition="Traits that increase the likelihood of survival and reproduction are most likely to be passed on to future generations."
            />
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Application:</strong> We still carry behaviors that helped our ancestors survive:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>Fearing snakes and heights</li>
              <li>Craving fats and sugars</li>
              <li>Stranger anxiety</li>
            </ul>
            <ImportantNote>
              <strong>Caution:</strong> Historically, some have misused evolutionary principles to justify discrimination (Eugenics). The AP course emphasizes understanding the biological mechanism, not endorsing discriminatory applications.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Twin and Adoption Studies</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Researchers use these studies to separate the effects of genetics and environment.
            </p>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Monozygotic Twins (Identical)" 
                definition="Develop from a single fertilized egg that splits. They share 100% of their DNA."
              />
              <KeyTerm 
                term="Dizygotic Twins (Fraternal)" 
                definition="Develop from two separate fertilized eggs. They share 50% of their DNA (same as regular siblings) but share a fetal environment."
              />
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900 mb-2">📊 Research Findings</h4>
              <ul className="list-disc pl-5 space-y-2 text-slate-700">
                <li>If identical twins are more alike on a trait (e.g., extraversion) than fraternal twins, the trait is likely heavily influenced by <strong>genetics</strong>.</li>
                <li><strong>Adoption Studies:</strong> Compare adopted children to their biological parents (genetics) and adoptive parents (environment).</li>
                <li>Personality traits tend to resemble biological parents, while values/manners/faith tend to resemble adoptive parents.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Overview of the Nervous System",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Central Nervous System (CNS)</h3>
            <KeyTerm 
              term="Components" 
              definition="The Brain and the Spinal Cord."
            />
            <KeyTerm 
              term="Function" 
              definition="The command center; makes decisions and coordinates body functions."
            />
            <KeyTerm 
              term="Spinal Cord" 
              definition="A two-way information highway connecting the peripheral nervous system and the brain. It also handles reflexes (automatic responses to stimuli) without consulting the brain first."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Peripheral Nervous System (PNS)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Connects the CNS to the rest of the body. It is divided into two main systems:
            </p>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-lg border border-green-200 mb-4">
              <h4 className="text-lg font-bold text-green-800 mb-3">1. Somatic Nervous System</h4>
              <ul className="list-disc pl-5 space-y-2 text-slate-700">
                <li>Controls the body's skeletal muscles</li>
                <li><strong>Voluntary</strong> movements (e.g., raising your hand, walking)</li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-5 rounded-lg border border-orange-200 mb-4">
              <h4 className="text-lg font-bold text-orange-800 mb-3">2. Autonomic Nervous System (ANS)</h4>
              <ul className="list-disc pl-5 space-y-2 text-slate-700 mb-4">
                <li>Controls the glands and muscles of internal organs (e.g., heart, lungs)</li>
                <li><strong>Involuntary</strong> ("Automatic") functions</li>
              </ul>
              
              <ImportantNote>
                <strong>The Dual System of the ANS (Crucial for AP Exam):</strong>
              </ImportantNote>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-red-100 p-4 rounded-lg border border-red-200">
                  <h5 className="font-bold text-red-800 mb-2">⚡ Sympathetic Nervous System</h5>
                  <p className="text-sm text-slate-700 mb-2">Arouses the body; mobilizes energy in stressful situations.</p>
                  <p className="text-sm font-semibold text-red-700 mb-2">"Fight or Flight" Response:</p>
                  <ul className="list-disc pl-4 text-xs text-slate-600 space-y-1">
                    <li>Pupils dilate</li>
                    <li>Heartbeat accelerates</li>
                    <li>Digestion inhibits</li>
                    <li>Glucose is released</li>
                    <li>Epinephrine (adrenaline) is secreted</li>
                  </ul>
                </div>
                <div className="bg-blue-100 p-4 rounded-lg border border-blue-200">
                  <h5 className="font-bold text-blue-800 mb-2">😌 Parasympathetic Nervous System</h5>
                  <p className="text-sm text-slate-700 mb-2">Calms the body; conserves energy.</p>
                  <p className="text-sm font-semibold text-blue-700 mb-2">"Rest and Digest" Response:</p>
                  <ul className="list-disc pl-4 text-xs text-slate-600 space-y-1">
                    <li>Pupils contract</li>
                    <li>Heartbeat slows</li>
                    <li>Digestion stimulates</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: The Neuron and Neural Firing",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Neural Structures</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <KeyTerm 
                term="Neurons" 
                definition="The nerve cells; the building blocks of the nervous system."
              />
              <KeyTerm 
                term="Glial Cells (Glia)" 
                definition="Cells that support, nourish, and protect neurons. They play a role in learning, thinking, and memory by maintaining the chemical environment."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. The Reflex Arc</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Three types of neurons communicate to process information (e.g., touching a hot stove):
            </p>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">1. Sensory (Afferent) Neurons</h4>
                <p className="text-sm text-slate-700 mt-1">Carry messages from the body's tissues/sensory receptors <strong>inward</strong> to the brain/spinal cord.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">2. Interneurons</h4>
                <p className="text-sm text-slate-700 mt-1">Located within the brain and spinal cord; they process information between sensory inputs and motor outputs.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">3. Motor (Efferent) Neurons</h4>
                <p className="text-sm text-slate-700 mt-1">Carry instructions from the CNS <strong>out</strong>to the body's muscles and glands.</p>
              </div>
            </div>
            <Mnemonic 
              title="SAME" 
              content="Sensory = Afferent, Motor = Efferent"
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Neural Transmission (The Action Potential)</h3>
            <ImportantNote>
              Communication <em>within</em> a neuron is <strong>electrical</strong>; communication <em>between</em> neurons is <strong>chemical</strong>.
            </ImportantNote>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Resting Potential</h4>
                  <p className="text-sm text-slate-600 mt-1">The neuron is polarized (negative charge inside, positive outside). It is ready to fire.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Threshold</h4>
                  <p className="text-sm text-slate-600 mt-1">The level of stimulation required to trigger a neural impulse.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="bg-amber-200 text-amber-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-amber-800">All-or-None Principle ⚡</h4>
                  <p className="text-sm text-slate-700 mt-2">A neuron either fires completely or not at all. It does not fire "stronger" or "weaker."</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Action Potential (Depolarization)</h4>
                  <p className="text-sm text-slate-600 mt-2">A neural impulse; a brief electrical charge that travels down an axon. Positive ions (sodium) rush in, creating a chain reaction.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Refractory Period</h4>
                  <p className="text-sm text-slate-600 mt-1">A brief pause after firing when the neuron pumps positive ions back out. It cannot fire again until this is reset.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">6</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Reuptake</h4>
                  <p className="text-sm text-slate-600 mt-1">The reabsorption of excess neurotransmitters by the sending neuron.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Neurotransmitters</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Chemical messengers that cross the <strong>synaptic gap</strong> between neurons. They bind to receptor sites on the receiving neuron like a key in a lock.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">✅ Excitatory</h4>
                <p className="text-sm text-slate-700 mt-1">"Go" signals (make firing more likely)</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🛑 Inhibitory</h4>
                <p className="text-sm text-slate-700 mt-1">"Stop" signals (make firing less likely)</p>
              </div>
            </div>

            <h4 className="text-lg font-bold text-slate-800 mb-4 mt-6">Key Neurotransmitters to Know:</h4>
            <div className="grid md:grid-cols-2 gap-3">
              <NeurotransmitterCard 
                name="Acetylcholine (ACh)" 
                functions="Muscle action, learning, memory"
                issues="Alzheimer's disease—ACh producing neurons deteriorate"
              />
              <NeurotransmitterCard 
                name="Dopamine" 
                functions="Movement, learning, attention, emotion/reward"
                issues="Excess: Schizophrenia; Deficit: Parkinson's disease"
              />
              <NeurotransmitterCard 
                name="Serotonin" 
                functions="Mood, hunger, sleep, arousal"
                issues="Deficit: Depression"
              />
              <NeurotransmitterCard 
                name="Norepinephrine" 
                functions="Alertness and arousal"
                issues="Deficit: Depressed mood"
              />
              <NeurotransmitterCard 
                name="GABA" 
                functions="Major inhibitory neurotransmitter"
                issues="Deficit: Seizures, tremors, insomnia"
              />
              <NeurotransmitterCard 
                name="Glutamate" 
                functions="Major excitatory neurotransmitter; involved in memory"
                issues="Excess: Migraines, seizures"
              />
              <NeurotransmitterCard 
                name="Endorphins" 
                functions="'Morphine within'; pain control and pleasure"
              />
              <NeurotransmitterCard 
                name="Substance P" 
                functions="Involved in the transmission of pain signals"
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">E. Hormones</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Chemical messengers of the endocrine system (slow chemical communication system).
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              <KeyTerm term="Adrenaline (Epinephrine)" definition="Fight or flight response" />
              <KeyTerm term="Oxytocin" definition="Bonding, trust, social connection" />
              <KeyTerm term="Cortisol" definition="Stress hormone" />
              <KeyTerm term="Melatonin" definition="Sleep regulation" />
              <KeyTerm term="Ghrelin/Leptin" definition="Hunger signals" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">F. Psychoactive Drugs</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Chemicals that change perceptions and mood by interacting with neural transmission.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Agonists</h4>
                <p className="text-sm text-slate-700 mt-1">Molecules that mimic a neurotransmitter and bind to its receptor to activate it (or block reuptake).</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Antagonists</h4>
                <p className="text-sm text-slate-700 mt-1">Molecules that block a neurotransmitter's functioning.</p>
              </div>
            </div>
            
            <h4 className="text-lg font-bold text-slate-800 mb-3 mt-6">Drug Categories:</h4>
            <div className="space-y-3">
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h5 className="font-bold text-yellow-800">⬆️ Stimulants</h5>
                <p className="text-sm text-slate-700">Excite neural activity</p>
                <p className="text-xs text-slate-500 mt-1">Examples: Caffeine, Nicotine, Cocaine, Amphetamines</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h5 className="font-bold text-indigo-800">⬇️ Depressants</h5>
                <p className="text-sm text-slate-700">Reduce neural activity/slow body functions</p>
                <p className="text-xs text-slate-500 mt-1">Examples: Alcohol, Barbiturates, Opiates</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h5 className="font-bold text-pink-800">🌀 Hallucinogens</h5>
                <p className="text-sm text-slate-700">Distort perceptions</p>
                <p className="text-xs text-slate-500 mt-1">Examples: LSD, Marijuana/THC</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">💊 Opioids</h5>
                <p className="text-sm text-slate-700">Depressants specifically used for pain relief</p>
                <p className="text-xs text-slate-500 mt-1">Examples: Heroin, Morphine</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: The Brain",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Tools of Discovery</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <KeyTerm 
                term="Lesioning" 
                definition="Destroying tissue to study the effect on behavior (e.g., H.M. case study)."
              />
              <KeyTerm 
                term="EEG (Electroencephalogram)" 
                definition="Records electrical waves across the brain's surface (sleep studies)."
              />
              <KeyTerm 
                term="CT/MRI" 
                definition="Structural imaging (shows anatomy/damage)."
              />
              <KeyTerm 
                term="PET/fMRI" 
                definition="Functional imaging (shows brain activity/blood flow/glucose consumption)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Older Brain Structures (Hindbrain/Brainstem)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These handle <strong>basic survival functions</strong>.
            </p>
            <div className="space-y-3">
              <BrainRegionCard name="Medulla" functions="Controls heartbeat and breathing." />
              <BrainRegionCard name="Pons" functions="Helps coordinate movement and controls sleep." />
              <BrainRegionCard name="Reticular Formation" functions="A nerve network involved in arousal and alertness." />
              <BrainRegionCard name="Thalamus" functions="The brain's sensory control center (all senses except smell)." />
              <BrainRegionCard name="Cerebellum" functions="'Little brain'; coordinates voluntary movement, balance, and nonverbal learning/memory." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. The Limbic System</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Associated with <strong>emotions and drives</strong>.
            </p>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🔴 Amygdala</h4>
                <p className="text-sm text-slate-700 mt-2">Linked to emotion, specifically <strong>aggression and fear</strong>.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🔶 Hypothalamus</h4>
                <p className="text-sm text-slate-700 mt-2">Maintenance activities (eating, drinking, body temp), governs the endocrine system via the pituitary gland, linked to emotion/reward.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🔵 Hippocampus</h4>
                <p className="text-sm text-slate-700 mt-2">Processes conscious, explicit memories.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. The Cerebral Cortex</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The body's ultimate control and information-processing center. Divided into <strong>two hemispheres</strong> and <strong>four lobes</strong>.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🧠 Frontal Lobes</h4>
                <p className="text-sm text-slate-700 mt-2">Speaking, muscle movements, plans, and judgment (Executive Function).</p>
                <p className="text-xs text-blue-700 mt-2 font-medium">Contains the Motor Cortex</p>
              </div>
              <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🖐️ Parietal Lobes</h4>
                <p className="text-sm text-slate-700 mt-2">Receives sensory input for touch and body position.</p>
                <p className="text-xs text-green-700 mt-2 font-medium">Contains the Somatosensory Cortex</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">👁️ Occipital Lobes</h4>
                <p className="text-sm text-slate-700 mt-2">Receive information from the visual fields.</p>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">👂 Temporal Lobes</h4>
                <p className="text-sm text-slate-700 mt-2">Auditory areas (hearing).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">E. Language Areas (Left Hemisphere)</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <h4 className="font-bold text-teal-800">🗣️ Broca's Area</h4>
                <p className="text-sm text-slate-700 mt-2">Controls language <strong>expression</strong> (muscle movements for speech).</p>
                <div className="bg-teal-100 p-2 rounded mt-2">
                  <p className="text-xs text-teal-800"><strong>Damage = Broca's Aphasia:</strong> Can comprehend, cannot speak fluently.</p>
                </div>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h4 className="font-bold text-cyan-800">🔊 Wernicke's Area</h4>
                <p className="text-sm text-slate-700 mt-1">Controls language <strong>reception</strong> (comprehension).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">F. Brain Plasticity</h3>
            <KeyTerm 
              term="Brain Plasticity" 
              definition="The brain's ability to change, especially during childhood, by reorganizing after damage or by building new pathways based on experience."
            />
            <KeyTerm 
              term="Neurogenesis" 
              definition="The formation of new neurons."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">G. Split-Brain Research</h3>
            <KeyTerm 
              term="Corpus Callosum" 
              definition="The band of neural fibers connecting the two hemispheres."
            />
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <h4 className="font-bold text-slate-800 mb-2">📚 Research (Sperry/Gazzaniga)</h4>
              <p className="text-sm text-slate-700 mb-2">When severed (to treat epilepsy), the two hemispheres cannot communicate.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Left Hemisphere</h4>
                <p className="text-sm text-slate-700 mt-1">Speech, math, logic.</p>
                <p className="text-xs text-blue-600 mt-1">(Controls right side of body)</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-800">Right Hemisphere</h4>
                <p className="text-sm text-slate-700 mt-1">Perceptual tasks, faces, spatial reasoning.</p>
                <p className="text-xs text-pink-600 mt-1">(Controls left side of body)</p>
              </div>
            </div>
            <ImportantNote>
              <strong>Split-Brain Effect:</strong> If a picture is flashed to the LEFT visual field (processed by Right Brain), the patient <em>cannot name it</em> (because Left Brain has language) but can <em>point to it</em> with their left hand.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Sleep",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Circadian Rhythm</h3>
            <KeyTerm 
              term="Circadian Rhythm" 
              definition="The biological clock; regular bodily rhythms (e.g., temperature and wakefulness) that occur on a 24-hour cycle."
            />
            <p className="text-slate-700 leading-relaxed mt-4">
              Regulated by the <strong>suprachiasmatic nucleus (SCN)</strong> in the hypothalamus, which causes the <strong>pineal gland</strong> to adjust <strong>melatonin</strong> production.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Sleep Stages</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Measured via EEG. A full cycle takes about <strong>90 minutes</strong>.
            </p>
            <div className="space-y-3">
              <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-800">NREM-1 (Stage 1)</h4>
                <p className="text-sm text-slate-700 mt-1">Transition to sleep. Slow breathing, irregular brain waves. <strong>Hypnagogic sensations</strong> (falling feeling).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-800">NREM-2 (Stage 2)</h4>
                <p className="text-sm text-slate-700 mt-1">Relaxed awake state. <strong>Sleep Spindles</strong> (bursts of rapid, rhythmic brain-wave activity). You spend the most time here.</p>
              </div>
              <div className="bg-violet-50 p-4 rounded-lg border-l-4 border-violet-400">
                <h4 className="font-bold text-violet-800">NREM-3 (Stage 3 - Deep Sleep)</h4>
                <p className="text-sm text-slate-700 mt-1">Deep sleep. Slow <strong>Delta Waves</strong>. Hard to awaken. Bedwetting/sleepwalking occur here.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-400">
                <h4 className="font-bold text-pink-800">REM (Rapid Eye Movement)</h4>
                <p className="text-sm text-slate-700 mt-1">Dreaming occurs. Heart rate rises, breathing becomes rapid.</p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-pink-700"><strong>Paradoxical Sleep:</strong> The muscles are relaxed (paralyzed) but other body systems are active.</p>
                  <p className="text-xs text-pink-700"><strong>REM Rebound:</strong> Increased REM sleep following sleep deprivation.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Sleep Disorders</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <KeyTerm term="Insomnia" definition="Persistent problems in falling or staying asleep." />
              <KeyTerm term="Narcolepsy" definition="Uncontrollable sleep attacks (often lapsing directly into REM)." />
              <KeyTerm term="Sleep Apnea" definition="Temporary cessations of breathing during sleep and repeated momentary awakenings." />
              <KeyTerm term="Night Terrors" definition="High arousal and appearance of being terrified (NREM-3). Distinct from nightmares (REM)." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Theories of Dreaming</h3>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Freud's Wish Fulfillment</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Manifest content</strong> = story line; <strong>Latent content</strong> = hidden meaning.</p>
                <p className="text-xs text-purple-600 mt-1 italic">Note: The CED excludes specific psychoanalytic theory details, but knowing it's unscientific is good context.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Information Processing/Consolidation</h4>
                <p className="text-sm text-slate-700 mt-1">Dreams help sort the day's experiences and consolidate memories.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Activation-Synthesis</h4>
                <p className="text-sm text-slate-700 mt-1">The brain attempts to make sense of random neural activity coming from the brainstem.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Sensation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Principles of Sensation</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Sensation" 
                definition="The process by which sensory receptors receive stimulus energies from the environment (Bottom-up processing)."
              />
              <KeyTerm 
                term="Transduction" 
                definition="Transforming stimulus energies (sights, sounds) into neural impulses the brain can interpret."
              />
              <KeyTerm 
                term="Absolute Threshold" 
                definition="The minimum stimulation needed to detect a stimulus 50% of the time."
              />
              <KeyTerm 
                term="Difference Threshold (JND)" 
                definition="The minimum difference a person can detect between any two stimuli 50% of the time."
              />
            </div>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mt-4">
              <h4 className="font-bold text-amber-800">Weber's Law</h4>
              <p className="text-sm text-slate-700 mt-1">To be perceived as different, two stimuli must differ by a constant minimum <strong>percentage</strong>, not a constant amount.</p>
            </div>
            <KeyTerm 
              term="Sensory Adaptation" 
              definition="Diminished sensitivity as a consequence of constant stimulation (e.g., getting used to a bad smell)."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Vision</h3>
            <h4 className="text-lg font-semibold text-slate-700 mb-3">1. The Eye</h4>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <KeyTerm term="Cornea/Pupil/Iris" definition="Light enters the eye." />
              <KeyTerm term="Lens" definition="Focuses light (accommodation)." />
              <KeyTerm term="Retina" definition="The inner surface containing receptor rods and cones." />
              <KeyTerm term="Fovea" definition="Central point of focus in the retina (cluster of cones)." />
              <KeyTerm term="Blind Spot" definition="Where the optic nerve leaves the eye (no receptors)." />
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">2. Photoreceptors</h4>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-800">🌙 Rods</h5>
                <p className="text-sm text-slate-700 mt-1">Detect black, white, and gray; necessary for peripheral and twilight vision.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-800">🌈 Cones</h5>
                <p className="text-sm text-slate-700 mt-1">Detect fine detail and color; function in daylight.</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3 mt-6">3. Color Theories</h4>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 via-green-50 to-blue-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-800">Trichromatic Theory (Young-Helmholtz)</h5>
                <p className="text-sm text-slate-700 mt-1">Retina contains three different color receptors (Red, Green, Blue).</p>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h5 className="font-bold text-cyan-800">Opponent-Process Theory</h5>
                <p className="text-sm text-slate-700 mt-1">Opposing retinal processes (Red-Green, Yellow-Blue, White-Black) enable color vision. Explains <strong>afterimages</strong>.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Hearing (Audition)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800 mb-2">Sound Waves</h4>
              <p className="text-sm text-slate-700"><strong>Amplitude</strong> determines loudness; <strong>Frequency</strong> determines pitch.</p>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-1">The Ear Structure</h4>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <KeyTerm term="Outer Ear" definition="Pinna, Auditory Canal" />
              <KeyTerm term="Middle Ear" definition="Eardrum, Ossicles (Hammer, Anvil, Stirrup)" />
              <KeyTerm term="Inner Ear" definition="Cochlea (fluid-filled coiled tube)" />
            </div>

            <KeyTerm 
              term="Transduction" 
              definition="Vibrations cause the basilar membrane in the cochlea to ripple, bending hair cells (cilia), which triggers neural impulses."
            />

            <h4 className="text-lg font-semibold text-slate-700 mb-3 mt-6">Pitch Theories</h4>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">Place Theory</h5>
                <p className="text-sm text-slate-700 mt-1">We hear different pitches because different sound waves trigger activity at different <strong>places</strong> along the cochlea's basilar membrane. (High pitches)</p>
              </div>
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <h5 className="font-bold text-teal-800">Frequency Theory</h5>
                <p className="text-sm text-slate-700 mt-1">The rate of nerve impulses traveling up the auditory nerve matches the frequency of a tone. (Low pitches)</p>
              </div>
            </div>

            <KeyTerm 
              term="Sound Localization" 
              definition="We identify sound direction based on which ear hears the sound first and loudest."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Chemical Senses</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-800">👅 Gustation (Taste)</h4>
                <p className="text-sm text-slate-700 mt-1">Sweet, sour, salty, bitter, umami. Receptors reproduce every week or two.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">👃 Olfaction (Smell)</h4>
                <p className="text-sm text-slate-700 mt-1">Chemical molecules breathed in through the nose.</p>
                <ImportantNote>
                  <strong>Unique feature:</strong> Neural pathways for smell bypass the thalamus and go directly to the olfactory bulb near the limbic system (strong link to memory/emotion).
                </ImportantNote>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">E. Body Senses</h3>
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">✋ Somatosensation (Touch)</h4>
                <p className="text-sm text-slate-700 mt-1">Pressure, warmth, cold, pain.</p>
                <div className="bg-orange-100 p-3 rounded mt-2">
                  <p className="text-xs text-orange-800"><strong>Gate-Control Theory:</strong> The spinal cord contains a neurological "gate" that blocks pain signals or allows them to pass to the brain.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-bold text-blue-800">⚖️ Vestibular Sense</h4>
                  <p className="text-sm text-slate-700 mt-1">The sense of body movement and position, including balance. Located in the <strong>semicircular canals</strong> of the inner ear.</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h4 className="font-bold text-purple-800">🦵 Kinesthesis</h4>
                  <p className="text-sm text-slate-700 mt-1">The system for sensing the position and movement of individual body parts (receptors in joints/tendons).</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🌈 Synesthesia</h4>
                <p className="text-sm text-slate-700 mt-1">A neurological condition where stimulation of one sensory pathway leads to automatic, involuntary experiences in a second sensory pathway (e.g., seeing numbers as colors).</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default psychologyUnit1Notes;
